import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, type InputProps, Space } from 'antd';
import { type FormItemProps } from 'antd/es/form';
import { FieldArray, useField } from 'formik';
import FormItem from './FormItem';
import InputField from './InputField';

export interface InputFieldArrayProps extends Omit<InputProps, 'value' | 'onChange' | 'autoComplete'> {
    name: string;
    label?: string;
    labelForAdd?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    limit?: number;
}

const InputFieldArray = ({ name, label, labelForAdd, itemProps, limit, ...props }: InputFieldArrayProps) => {
    const [field, meta] = useField({ name });
    const { value } = meta;

    return (
        <FormItem meta={meta} {...itemProps} label={label} name={name}>
            <FieldArray
                {...field}
                name={name}
                render={arrayHelpers => {
                    if (!value || value.length === 0) {
                        arrayHelpers.push('');
                    }

                    return (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {value.map((key: string, index: number) => (
                                <InputField
                                    key={`${name}.${index.toString()}`}
                                    addonAfter={
                                        value.length > 1 ? (
                                            <DeleteOutlined
                                                onClick={() => arrayHelpers.remove(index)}
                                                style={{ color: 'red' }}
                                            />
                                        ) : null
                                    }
                                    name={`${name}.${index}`}
                                />
                            ))}
                            {limit && value.length >= limit - 1 ? null : (
                                <Button
                                    icon={<PlusOutlined />}
                                    onClick={() => {
                                        arrayHelpers.push('');
                                    }}
                                    type="link"
                                >
                                    {labelForAdd}
                                </Button>
                            )}
                        </Space>
                    );
                }}
            />
        </FormItem>
    );
};

export default InputFieldArray;
